import Header from '../../../../components/Header';
import Footer from '../../../../components/Footer/Footer';
import { MMANavbar } from '../../../../components/Tools/Navbar/Navbar';
import { Breadcrumb, Tab, Tabs, Table } from 'react-bootstrap';
import { FaHome } from 'react-icons/fa';

import 'bootstrap/dist/css/bootstrap.min.css';

const SITroubleshootOverview = () => {
	return (
		<>
			<Header plant="montgomery" />
			<main>
				<wrapper class="section-wrapper">
					<MMANavbar />
					<container class="section-container">
						<Breadcrumb>
							<Breadcrumb.Item href="/smart-inspect/montgomery/overview">
								<FaHome size={20} />
							</Breadcrumb.Item>
							<Breadcrumb.Item active>Troubleshooting</Breadcrumb.Item>
							<Breadcrumb.Item active>Electrical Troubleshooting</Breadcrumb.Item>
						</Breadcrumb>
						<content class="section-content">
							<Tabs defaultActiveKey="Electrical Troubleshooting Overview" className="mb-3">
								<Tab eventKey="Electrical Troubleshooting Overview" title="Electrical Troubleshooting Overview">
									<h2>ELECTRICAL TROUBLESHOOTING OVERVIEW</h2>

									<p>Electrical troubleshooting is the process of locating the cause of malfunctions in electrical circuits. The following pages contain some general troubleshooting information as well as specific tests for determining the status of specific electrical devices. Skill in troubleshooting electrical equipment and circuits requires the following expertise:</p>

									<ul>
										<li>Knowledge of electrical principles to understand how a circuit or device should function.</li>
										<li>Skill in reading and interpreting electrical schematics, diagrams, product data, and so forth.</li>
										<li>Skill in operating test equipment and interpreting test measurements.</li>
										<li>Ability to analyze problems in a logical manner.</li>
									</ul>

									<p>Following systematic steps that narrow down the problem to a smaller area of the equipment is much more efficient than trial-and-error methods. The troubleshooting procedure detailed below can be very useful in organizing the problem-solving effort and reducing equipment downtime. The following troubleshooting procedure consists of five steps that you should perform in sequence. These steps represent the most reliable method of learning and applying a logical approach to problem solving and can be applied to any equipment, regardless of size.</p>
								</Tab>

								<Tab eventKey="Electrical Troubleshooting Procedure" title="Electrical Troubleshooting Procedure">
									<h2>ELECTRICAL TROUBLESHOOTING PROCEDURE</h2>
									<Table striped bordered hover>
										<thead>
											<tr>
												<th>Step</th>
												<th>Action</th>
											</tr>
										</thead>
										<tbody>
											<tr>
												<td>1</td>
												<td>
													<h6>Identify the symptom.</h6>
													<p>A symptom is an external indication that a circuit or device is not functioning correctly. You can identify a symptom by investigating the problem by sight, sound, smell, and touch. For example, visually inspecting the equipment may reveal that a circuit component has overheated and changed color or that an indicator lamp, which should be on, is not. A peculiar odor may lead you to discover melted insulation, or a chattering noise could indicate that a solenoid is about to fail. Moving controls or adjusting knobs may change the problem or have no effect at all. The fact that the equipment is not operating is a symptom. If someone else was operating the equipment when it failed, ask the person if he/she noticed anything unusual before it failed. Funny noises, things that do not look quite right, and improper operating sequences are symptoms that could lead to the cause of the problem. If you cannot find any immediately identifiable symptoms, try operating the equipment once you determine that it is safe to do so. Watch what works and what does not work. Note anything that does not seem right, no matter how small. Take the time to conduct a thorough investigation.</p>
												</td>
											</tr>
											<tr>
												<td>2</td>
												<td>
													<h6>Analyze the symptom.</h6>
													<p>In this step, you identify the functions where symptoms indicate a malfunction. Use the information you obtained during your identification, along with the schematic and functional block diagrams and knowledge of how the equipment is supposed to operate, to make logical, technical deductions. For example, after careful examination, you find that a clamp will not pressurize. Further analysis, without using test equipment, narrows the problem to clamp pressurization or pre-fill shift, any of which might contain the faulty circuit.</p>
												</td>
											</tr>
											<tr>
												<td>3</td>
												<td>
													<h6>Isolate the single faulty function.</h6>
													<p>In this step, you use test equipment to decide which faulty function is actually causing the malfunction.</p>
													<p>Testing electrical equipment can be hazardous. The electrical energy contained in many circuits can be enough to injure or kill. Make sure you follow all of your company’s safety precautions, rules and procedures while troubleshooting.</p>
													<p>When making these tests, use the following guidelines:</p>

													<ul>
														<li>Make only those tests that are safe to make.</li>
														<li>Make the least difficult tests first.</li>
														<li>Test those functions first that will eliminate one or more of the other possible faulty functions.</li>
													</ul>

													<p>For example, if taking an ohmmeter reading can determine the fault; do not take a voltmeter reading, as that requires power on the equipment. If you must disassemble part of the machine to reach a test point, perform a simpler test first. Test at a midway point in the circuitry, if possible. A good reading at the midway point eliminates the preceding functions and indicates that the problem is in the remaining circuits. A faulty signal at the midway point means that the problem is in the functions that process the signal before the midway point.</p>

													<p>In the previous clamp example, test the clamp's pressurization circuits where the clamp's fully retracted signal input either eliminates that function or confirms that the cause of the problem is a clamp that is not fully returned and, therefore, cannot be pressurized. Continue testing inputs and outputs of the suspect functions until you identify and confirm the single faulty function.</p>
												</td>
											</tr>
											<tr>
												<td>4</td>
												<td>
													<h6>Isolate the faulty circuit.</h6>
													<p>In this step, you locate the single malfunctioning circuit within a functional group of circuits. Use the accumulated symptom and test data to close in on the single faulty circuit. Follow the guidelines from Step Three, but apply them to the circuits related to the faulty function. Use schematic and block diagrams to locate test points.</p>

													<p>In the clamp example, assume that the clamp's fully retracted signal is not present at the input to the clamp's pressurization circuits. Test within the clamp's retracted circuits until you identify a single faulty circuit. The first test may reveal that the output of the clamp's fully retracted circuit is bad. A check of the inputs to this circuit may indicate that the input from a clamp's retracted- switch is bad but that all others are good. You can now identify the problem as being associated with one of the relatively few parts contained in a single circuit.</p>
												</td>
											</tr>
											<tr>
												<td>5</td>
												<td>
													<h6>Locate / verify the cause of the malfunction.</h6>

													<p>The tests you make in this step identify the failing part within the faulty circuit. Test the circuit until you find the cause of the malfunction. Examine and test the faulty part to verify that it has caused the problem and produced the observed symptoms.</p>
												</td>
											</tr>
										</tbody>
									</Table>
								</Tab>

								<Tab eventKey="Sequence Stops" title="Sequence Stops">
									<h2>SEQUENCE STOPS</h2>

									<p>Sequence stops encompass a broad range of problems, most of which only require simple repair. Most sequence stops can be traced to a limit or proximity switch that has not been tripped. A logic controller network controls the sequencing of components. When an input is received, it initializes the appropriate output based on current system logic.</p>

									<p>These sensor devices are in use constantly during machine operation. Most problems with sequencing can be attributed to dirty, faulty, or maladjusted switches. In less common cases, sequence stops are due to an output device not activating. Problems here can range from failed actuators to a logic failure. Isolating either problem can be done quickly with a basic understanding of machine operation and station controls.</p>

									<p>Problems not attributed to output device failure may be the cause of a physical disconnection. Look for broken air lines, loose cable connections, or mechanical obstruction. Mechanical obstruction can often take the form of debris buildup on, or near sensors. In the event of sensor failure, examine the actuating device to determine if the stop occurred before or after the midrange point of the actuated device. If the stop occurs before midpoint, this indicates an advance sensor failure. A stop past midpoint signifies a retract sensor failure. If the problem cannot be physically identified, use wiring diagrams to locate the output module for the actuating device.</p>
								</Tab>

								<Tab eventKey="Repairing Sensors" title="Repairing Sensors">
									<h2>REPAIRING SENSORS</h2>

									<p>Prior to repairing a sensor, ensure that the related actuator has traveled full stroke to the correct physical trip position. In some cases the sensor may be fully operational but the actuator motion is limited by an obstruction.</p>

									<p>Return the actuator to the opposite position of the failure. Manually trip the sensor and observe the indicator on the I/O card to see if the sensor is operating correctly. If no indication is observed, replace the sensor and repeat the test. If there is still no indication, the problem exists in the wiring between the controller and the sensor.</p>

									<p>Manually operate the actuator several times to ensure proper adjustment of the sensor.</p>
								</Tab>

								<Tab eventKey="Non-Actuating Problems" title="Non-Actuating Problems">
									<h2>NON-ACTUATING PROBLEMS</h2>

									<p>After examining the system for obvious physical breaks, attempt to operate the device using manual controls. Manual operation may also reveal the presence of an obstruction. If it is determined that the failed component is receiving proper pneumatic pressure and there is no obstruction, the problem is controls related. If the problem is determined to be controls based, it exists somewhere between the cable system and the controller. First, determine if the controller is receiving power. If the power supplies are functional, proceed to the next step. Be aware that a malfunction of the power supply may have caused damage to other components.</p>
								</Tab>

								<Tab eventKey="Programmable Logic Controller (PLC)" title="Programmable Logic Controller (PLC)">
									<h2>PROGRAMMABLE LOGIC CONTROLLER</h2>

									<p>
										Troubleshooting is a straightforward process in automated systems. When you encounter a problem, remember that less than one-third of all system failures will be due to the <b>PLC</b>. Most of the failures are due to input and output devices. Often the error checking capabilities present on the <b>PLC</b> modules are sufficient for fault diagnosis. The light emitting diodes (LEDs) on <b>PLC</b> central processing units and modules can provide immediate feedback on what is happening. <b>PLC</b>s are equipped with light emitting diodes (LEDs) to indicate blown fuses and other problems. Examine these indicators first.
									</p>
								</Tab>

								<Tab eventKey="PLC Inputs" title="PLC Inputs">
									<h2>PLC INPUT TROUBLESHOOTING PROCEDURE</h2>

									<p>If the field device connected to an input module does not seem to turn ON, a problem may exist somewhere between the L1 connection and the terminal connection to the module. An input module’s status indicators can provide information about the field device, the module, and the field device’s wiring to the module that will help pinpoint the problem.</p>

									<Table striped bordered hover>
										<thead>
											<tr>
												<th>Step</th>
												<th>Action</th>
											</tr>
										</thead>
										<tbody>
											<tr>
												<td>1</td>
												<td>
													The first step in diagnosing the problem is to place the <b>PLC</b> in standby mode, so that it is not activating the output. This allows the field device to be manually activated (e.g., a limit switch can be manually closed). When the field device is activated, the module’s power status indicator should turn on, indicating that power continuity exists. If the indicator is on, then wiring is not the cause of the problem.
												</td>
											</tr>
											<tr>
												<td>2</td>
												<td>
													Try using the <b>PLC</b>’s test mode, which reads the inputs and executes the program but does not activate the outputs. In this mode, the <b>PLC</b>’s display should either show a 1 in the image table bit corresponding to the activated field device or the contact’s reference instruction should become highlighted when the device provides continuity. If the <b>PLC</b> is reading the device correctly, then the problem is not located in the input module. If it does not read the device correctly, then the module could be faulty. The logic side of the module may not be operating correctly, or its optical isolator may be blown. Moreover, one of the module’s interfacing channels could be faulty. In this case, the module must be replaced.
												</td>
											</tr>
											<tr>
												<td>3</td>
												<td>If the module does not read the field device’s signal, then further tests are required. Bad wiring, a faulty field device, a faulty module, or an improper voltage between the field device and the module could be causing the problem. First, close the field device and measure the voltage to the input module. The meter should display the voltage of the signal (e.g., 120 volts AC). If the proper voltage is present, the input module is faulty because it is not recognizing the signal. If the measured voltage is 10–15% below the proper signal voltage, then the problem lies in the source voltage to the field device. If no voltage is present, then either the wiring or the field device is the cause of the problem. Check the wiring connection to the module to ensure that the wire is secured at the terminal or terminal blocks.</td>
											</tr>
											<tr>
												<td>4</td>
												<td>To further pinpoint the problem, check that voltage is present at the field device. With the device activated, measure the voltage across the device using a voltmeter. If no voltage is present on the load side of the device (the side that connects to the module), then the input device is faulty. If there is power, then the problem lies in the wiring from the input device to the module. In this case, the wiring must be traced to find the problem.</td>
											</tr>
										</tbody>
									</Table>
								</Tab>

								<Tab eventKey="PLC Outputs" title="PLC Outputs">
									<h2>PLC OUTPUT TROUBLESHOOTING PROCEDURE</h2>

									<p>
										<b>PLC</b> output interfaces also contain status indicators that provide useful troubleshooting information. Like the troubleshooting of <b>PLC</b> inputs, the first step in troubleshooting outputs is to isolate the problem to the module, the field device, or the wiring.
									</p>

									<Table striped bordered hover>
										<thead>
											<tr>
												<th>Step</th>
												<th>Action</th>
											</tr>
										</thead>
										<tbody>
											<tr>
												<td>1</td>
												<td>At the output module, ensure that the source power for switching the output is at the correct level. In a 120 VAC system, this value should be within 10% of the rated value (i.e., between 108 and 132 volts AC). Also, examine the output module to see if it has a blown fuse. If it does have a blown fuse, check the fuse’s rated value. Furthermore, check the output device’s current requirements to determine if the device is pulling too much current.</td>
											</tr>
											<tr>
												<td>2</td>
												<td>If the output module receives the command to turn on from the processor yet the module’s output status does not turn on accordingly, then the output module is faulty. If the indicator turns on but the field device does not energize, check for voltage at the output terminal to ensure that the switching device is operational. If no voltage is present, then the module should be replaced. If voltage is present, then the problem lies in the wiring or the field device. At this point, make sure that the field wiring to the module’s terminal or to the terminal block has a good connection and that no wires are broken.</td>
											</tr>
											<tr>
												<td>3</td>
												<td>After checking the module, check that the field device is working properly. Measure the voltage coming to the field device while the output module is on, making sure that the return line is well connected to the device. If there is power yet the device does not respond, then the field device is faulty.</td>
											</tr>
											<tr>
												<td>4</td>
												<td>Another method for checking the field device is to test it without using the output module. Remove the output wiring and connect the field device directly to the power source. If the field device does not respond, then it is faulty. If the field device responds, then the problem lies in the wiring between the device and the output module. Check the wiring, looking for broken wires along the wire path.</td>
											</tr>
										</tbody>
									</Table>
								</Tab>

								<Tab eventKey="PLC Processor" title="PLC Processor">
									<h2>PLC PROCESSOR TROUBLESHOOTING PROCEDURE</h2>

									<p>
										The <b>PLC</b> processor also provides diagnostic indicators that show the status of the <b>PLC</b> and the CPU. Such indicators include power OK, memory OK, and communications OK conditions.
									</p>

									<Table striped bordered hover>
										<thead>
											<tr>
												<th>Step</th>
												<th>Action</th>
											</tr>
										</thead>
										<tbody>
											<tr>
												<td>1</td>
												<td>
													First, check that the <b>PLC</b> is receiving enough power from the transformer to supply all the loads. If the <b>PLC</b> is still not working, check for voltage supply drop in the control circuit or for blown fuses.
												</td>
											</tr>
											<tr>
												<td>2</td>
												<td>
													If the <b>PLC</b> does not come up even with proper power, then the problem lies in the CPU. The diagnostic indicators on the Rear of the CPU will show a fault in either memory or communications. If one of these indicators is lit, the CPU may need to be replaced.
												</td>
											</tr>
										</tbody>
									</Table>

									<p>In conclusion, the best method for diagnosing input/output malfunctions is to isolate the problem to the module, the field device, or the wiring. If both power and logic indicators are available for review, then module failures will become readily apparent.</p>

									<p>The first step in solving the problem is to take a voltage measurement to determine if the proper voltage level is present at the input or output terminal. If the voltage is adequate at the terminal and the module is not responding, then the module should be replaced. If the replacement module has no effect, then field wiring may be the problem.</p>

									<p>A proper voltage level at the output terminal while the output device is off also indicates an error in the field wiring. If an output rung is activated but the LED indicator is off, then the module is faulty.</p>

									<p>If a malfunction cannot be traced to the I/O module, then the module connectors should be inspected for poor contact or misalignment. Finally, check for broken wires under connector terminals and cold solder joints on module terminals.</p>
								</Tab>

								<Tab eventKey="Allen Bradley GuardLogix" title="Allen Bradley GuardLogix">
									<h2>ALLEN BRADLEY GUARDLOGIX</h2>

									<p>Please refer to Allen Bradley’s ControlLogix 5580 and GuardLogix 5580 Controller User Manual (1756-um543 -en), section 14 – Troubleshooting the controller. These general procedures can be used to help personnel perform troubleshooting for the GuardLogix equipment used throughout the cell.</p>
								</Tab>

								<Tab eventKey="Control Panel" title="Control Panel">
									<h2>CONTROL PANEL TOUBLESHOOTING PROCEDURE</h2>

									<Table striped bordered hover>
										<thead>
											<tr>
												<th>Symptom</th>
												<th>Possible Cause</th>
												<th>Probable Solutions</th>
											</tr>
										</thead>
										<tbody>
											<tr>
												<td rowSpan={9}>Power will not come on.</td>
												<td>Disconnect is off.</td>
												<td>Verify disconnects on the main control panels are in the on position.</td>
											</tr>
											<tr>
												<td>Blown fuse.</td>
												<td>Check/replace fuse.</td>
											</tr>
											<tr>
												<td>Circuit breaker tripped.</td>
												<td>Check/reset circuit breaker.</td>
											</tr>
											<tr>
												<td>E-Stop string active.</td>
												<td>Check/reset E-Stops and safety gate plugs.</td>
											</tr>
											<tr>
												<td rowSpan={3}>Incoming power fault.</td>
												<td>Check incoming line power just before the disconnect.</td>
											</tr>
											<tr>
												<td>Check that the buss connection is made and condition of buss plug.</td>
											</tr>
											<tr>
												<td>Verify that buss has power by checking if other equipment on buss is operating.</td>
											</tr>
											<tr>
												<td rowSpan={2}>Disconnect faulty.</td>
												<td>Check the disconnect operating mechanism. Repair or replace, if necessary.</td>
											</tr>
											<tr>
												<td>Check condition of contacts. Clean or replace, if necessary.</td>
											</tr>
											<tr>
												<td rowSpan={3}>Control power will not come on.</td>
												<td>E-Stop active.</td>
												<td>Be sure that all E-Stop push buttons are reset.</td>
											</tr>
											<tr>
												<td>Fault present.</td>
												<td>Correct and clear fault.</td>
											</tr>
											<tr>
												<td>Blown fuse.</td>
												<td>Check/replace fuse.</td>
											</tr>
											<tr>
												<td rowSpan={19}>Master power will not come on.</td>
												<td>Bodyline interlock.</td>
												<td>
													Check <b>PLC</b> signal.
												</td>
											</tr>
											<tr>
												<td>Blown fuse(s).</td>
												<td>Check fuse(s). Replace, if required.</td>
											</tr>
											<tr>
												<td>Control transformer faulty.</td>
												<td>Check for voltage present on terminals. Replace transformer, if necessary.</td>
											</tr>
											<tr>
												<td>Control master relay.</td>
												<td>Check operation of master relay. Check contacts. Repair or replace, if necessary.</td>
											</tr>
											<tr>
												<td>E-Stop circuit active.</td>
												<td>Check/reset E-Stops, safety gate plugs or other safety interlocks. Reset.</td>
											</tr>
											<tr>
												<td rowSpan={14}>Proximity or limit switch.</td>
												<td>Check if supply voltage is present to the sensor. Check status light on input card.</td>
											</tr>
											<tr>
												<td>If voltage is not present, check power supply.</td>
											</tr>
											<tr>
												<td>Verify correct voltage is wired to device.</td>
											</tr>
											<tr>
												<td>Check all point to point connections.</td>
											</tr>
											<tr>
												<td>Check all cords and connectors. Swap out a cord to see if it’s faulty.</td>
											</tr>
											<tr>
												<td>Switch devices to see if it is faulty.</td>
											</tr>
											<tr>
												<td>Voltage spikes or transients may cause intermittent problems. Check for fluctuations in line current. Check power supply.</td>
											</tr>
											<tr>
												<td>Check equipment ground. Intermittent ground will cause problems.</td>
											</tr>
											<tr>
												<td>Verify correct voltage is wired to device.</td>
											</tr>
											<tr>
												<td>Check all point to point connections.</td>
											</tr>
											<tr>
												<td>Check all cords and connectors. Swap out a cord to see if it’s faulty.</td>
											</tr>
											<tr>
												<td>Switch devices to see if it is faulty.</td>
											</tr>
											<tr>
												<td>Voltage spikes or transients may cause intermittent problems. Check for fluctuations in line current. Check power supply.</td>
											</tr>
											<tr>
												<td>Check equipment ground. Intermittent ground will cause problems.</td>
											</tr>
										</tbody>
									</Table>
								</Tab>

								<Tab eventKey="Proximity / Limit Switch" title="Proximity / Limit Switch">
									<h2>PROXIMITY / LIMIT SWITCH TROUBLESHOOTING CHART</h2>

									<Table striped bordered hover>
										<thead>
											<tr>
												<th>Symptom</th>
												<th>Possible Cause</th>
												<th>Probable Solutions</th>
											</tr>
										</thead>
										<tbody>
											<tr>
												<td rowSpan={4}>Detection failure.</td>
												<td>Debris or foreign object on fingers.</td>
												<td>Clean fingers with damp cloth. Remove any foreign objects.</td>
											</tr>
											<tr>
												<td>Misalignment.</td>
												<td>Check finger for damage or bending. Replace as needed.</td>
											</tr>
											<tr>
												<td>Scratch on switch contacts.</td>
												<td>Replace proximity switch.</td>
											</tr>
											<tr>
												<td>Debris or foreign object obstructing wand.</td>
												<td>Remove obstructions or foreign object from wand.</td>
											</tr>
										</tbody>
									</Table>
								</Tab>

								<Tab eventKey="Electrical Device Inspection" title="Electrical Device Inspection">
									<h2>ELECTRICAL DEVICE INSPECTION</h2>

									<p>The following sections outline some basic electrical tests that you can conduct on specific equipment components. As part of a troubleshooting test, you should visually inspect the devices. Also, if spare parts are available, substitute a good part for a suspect part as a quick method of returning the equipment to operation. Test the suspect part and either replace, repair, or discard it.</p>

									<h5>SOLENOID COILS</h5>
									<Table striped bordered hover>
										<thead>
											<tr>
												<th>Step</th>
												<th>Action</th>
											</tr>
										</thead>
										<tbody>
											<tr>
												<td>1</td>
												<td>Remove it from the machine (plug ports on valves if required).</td>
											</tr>
											<tr>
												<td>2</td>
												<td>Disassemble and visually examine the solenoid for signs of overheating or mechanical problems.</td>
											</tr>
											<tr>
												<td>3</td>
												<td>Test the coil by attaching an ohmmeter (set to a low resistance range) across the coil terminals. A relatively low reading (a few thousand ohms or less) should be observed on the meter if the coil is good. It should not read zero ohms as this indicates the coil windings are shorted to each other, probably as a result of melted insulation. If the ohmmeter reads infinity, it means that the coil has opened up and is defective.</td>
											</tr>
										</tbody>
									</Table>

									<h5>CONTACT RELAYS</h5>
									<Table striped bordered hover>
										<thead>
											<tr>
												<th>Step</th>
												<th>Action</th>
											</tr>
										</thead>
										<tbody>
											<tr>
												<td>1</td>
												<td>Remove it from the machine.</td>
											</tr>
											<tr>
												<td>2</td>
												<td>Carefully examine the relay for mechanical problems.</td>
											</tr>
											<tr>
												<td>3</td>
												<td>If none are noted, check the relay coil in the same way as a solenoid coil. The electrical contacts can be tested with an ohmmeter in the same way that any switch contacts are tested. The meter should read zero when the contacts are closed and infinity when they are open.</td>
											</tr>
											<tr>
												<td>4</td>
												<td>Manually actuate the relay armature to conduct these tests and remember to test both the normally open and normally closed contact.</td>
											</tr>
										</tbody>
									</Table>

									<h5>CIRCUIT BREAKERS</h5>
									<Table striped bordered hover>
										<thead>
											<tr>
												<th>Step</th>
												<th>Action</th>
											</tr>
										</thead>
										<tbody>
											<tr>
												<td>1</td>
												<td>Remove dust, dirt, soot, grease, or moisture from the surface of the circuit breaker using a lint-free dry cloth, brush, or vacuum cleaner. Do not blow debris into circuit breakers. If contamination is found, look for the source and eliminate the problem.</td>
											</tr>
											<tr>
												<td>2</td>
												<td>Switch circuit breaker to on and off several times to be sure that the mechanical linkages are free and do not bind. If mechanical linkages are not free, replace the circuit breaker.</td>
											</tr>
											<tr>
												<td>3</td>
												<td>Check base, cover, and operating handle for cracks, chipping, and discoloration. The circuit breaker should be replaced if cracks or severe discoloration is found.</td>
											</tr>
											<tr>
												<td>4</td>
												<td>Check terminals and connectors for looseness or signs of overheating. Overheating will show as discoloration, melting, or blistering of conductor insulation, or as pitting or melting of conductor surfaces due to arcing. If there is no evidence of overheating or looseness, do not disturb or tighten the connections. If there is evidence of overheating, termination points should be cleaned or replaced. Before re-energizing the circuit breaker, all terminations and cables should be refurbished to the original condition they were in when first installed.</td>
											</tr>
											<tr>
												<td>5</td>
												<td>Check circuit breaker mounting hardware, and tighten if necessary.</td>
											</tr>
										</tbody>
									</Table>
								</Tab>
							</Tabs>
						</content>
					</container>
				</wrapper>
			</main>
			<Footer />
		</>
	);
};

export default SITroubleshootOverview;
